// app/src/core/api/hospitalFavoriteApi.ts

import { serverApi, handleApiResponse } from "@core/api/core"
import { HospitalFavorite, CreateHospitalFavoriteRequest, UpdateHospitalFavoriteRequest } from "@/types/hospitalFavorite"
import { TreatmentItemDef, DEFAULT_TREATMENT_ITEMS, DEFAULT_TREATMENT_TIMER_MINUTES } from "@/types/treatmentRoom"

const FAVORITE_BASE_URL = "/hospital/favorite/"

export type HospitalFavoriteListParams = Record<string, string | number | boolean | undefined>

export const getHospitalFavorites = async (params?: HospitalFavoriteListParams): Promise<HospitalFavorite[]> => {
  const response = await serverApi.get(FAVORITE_BASE_URL, { params })
  return handleApiResponse<HospitalFavorite[]>(response)
}

export const getHospitalFavorite = async (hospitalFavoriteUuid: string): Promise<HospitalFavorite> => {
  const response = await serverApi.get(`${FAVORITE_BASE_URL}${hospitalFavoriteUuid}/`)
  return handleApiResponse<HospitalFavorite>(response)
}

export const createHospitalFavorite = async (payload: CreateHospitalFavoriteRequest): Promise<HospitalFavorite> => {
  const response = await serverApi.post(FAVORITE_BASE_URL, payload)
  return handleApiResponse<HospitalFavorite>(response)
}

export const updateHospitalFavorite = async (hospitalFavoriteUuid: string, payload: UpdateHospitalFavoriteRequest): Promise<HospitalFavorite> => {
  const response = await serverApi.patch(`${FAVORITE_BASE_URL}${hospitalFavoriteUuid}/`, payload)
  return handleApiResponse<HospitalFavorite>(response)
}

export const replaceHospitalFavorite = async (hospitalFavoriteUuid: string, payload: CreateHospitalFavoriteRequest): Promise<HospitalFavorite> => {
  const response = await serverApi.put(`${FAVORITE_BASE_URL}${hospitalFavoriteUuid}/`, payload)
  return handleApiResponse<HospitalFavorite>(response)
}

export const deleteHospitalFavorite = async (hospitalFavoriteUuid: string): Promise<void> => {
  await serverApi.delete(`${FAVORITE_BASE_URL}${hospitalFavoriteUuid}/`)
}

// Treatment room favorites
export const DEFAULT_TREATMENT_BED_COUNT = 8
export const TREATMENT_ROOM_FAVORITE_NAME = "TreatmentRoomSettings"

export type TreatmentRoomBedAliasMap = Record<string, string>

export interface TreatmentRoomFavoriteData {
  bedCount?: number
  bedAliases?: TreatmentRoomBedAliasMap
  showTreatmentMemo?: boolean
  showDetachedRemainingOverlay?: boolean
  treatmentItems?: Array<{
    id: string
    name: string
    shortName: string
    order: number
    enabled: boolean
    timerMinutes?: number
  }>
}

export const clampBedCount = (value?: number): number => {
  const parsed = typeof value === "number" ? value : Number(value)
  if (!Number.isFinite(parsed)) return DEFAULT_TREATMENT_BED_COUNT
  const rounded = Math.round(parsed)
  return Math.min(50, Math.max(1, rounded))
}

export const normalizeTreatmentRoomBedNumber = (bedNumber?: string | number | null): string => {
  const normalized = String(bedNumber || "").trim()
  if (!normalized) {
    return ""
  }

  if (/^\d+$/.test(normalized)) {
    return String(Number(normalized))
  }

  return normalized
}

export const normalizeTreatmentRoomBedAliases = (bedAliases?: TreatmentRoomBedAliasMap | null): TreatmentRoomBedAliasMap => {
  if (!bedAliases || typeof bedAliases !== "object") {
    return {}
  }

  return Object.entries(bedAliases).reduce<TreatmentRoomBedAliasMap>((accumulator, [bedNumber, alias]) => {
    const normalizedBedNumber = normalizeTreatmentRoomBedNumber(bedNumber)
    const normalizedAlias = typeof alias === "string" ? alias.trim() : ""

    if (!normalizedBedNumber || !normalizedAlias) {
      return accumulator
    }

    accumulator[normalizedBedNumber] = normalizedAlias
    return accumulator
  }, {})
}

export const fetchTreatmentRoomFavorite = async (): Promise<{
  bedCount: number
  bedAliases: TreatmentRoomBedAliasMap
  showTreatmentMemo: boolean
  showDetachedRemainingOverlay: boolean
  treatmentItems: TreatmentItemDef[]
  favorite: HospitalFavorite | null
}> => {
  const favorites = await getHospitalFavorites()
  const favorite = favorites.find(item => item.hospitalFavoriteName === TREATMENT_ROOM_FAVORITE_NAME) || null
  const data = (favorite?.hospitalFavoriteData || {}) as TreatmentRoomFavoriteData
  const savedTreatmentItems = Array.isArray(data?.treatmentItems) ? data.treatmentItems : null
  const hasSavedTreatmentItems = savedTreatmentItems !== null
  const treatmentItems = hasSavedTreatmentItems ? savedTreatmentItems : DEFAULT_TREATMENT_ITEMS
  const normalizedItems = treatmentItems.map(item => ({
    ...item,
    // 기존 favorite 데이터에 timerMinutes가 없던 경우까지 10분으로 승격시키지 않는다.
    timerMinutes: typeof item.timerMinutes === "number" ? item.timerMinutes : hasSavedTreatmentItems ? 0 : DEFAULT_TREATMENT_TIMER_MINUTES,
  }))
  return {
    bedCount: clampBedCount(data?.bedCount),
    bedAliases: normalizeTreatmentRoomBedAliases(data?.bedAliases),
    showTreatmentMemo: !!data?.showTreatmentMemo,
    showDetachedRemainingOverlay: !!data?.showDetachedRemainingOverlay,
    treatmentItems: normalizedItems,
    favorite,
  }
}

export const saveTreatmentRoomFavorite = async (
  bedCount: number,
  favorite: HospitalFavorite | null,
  treatmentItems?: TreatmentItemDef[],
  bedAliases?: TreatmentRoomBedAliasMap,
  options?: { showTreatmentMemo?: boolean; showDetachedRemainingOverlay?: boolean }
): Promise<HospitalFavorite> => {
  const sanitized = clampBedCount(bedCount)
  const previousFavoriteData = ((favorite?.hospitalFavoriteData || {}) as TreatmentRoomFavoriteData) || {}
  const favoriteData: TreatmentRoomFavoriteData = {
    ...previousFavoriteData,
    bedCount: sanitized,
    bedAliases: normalizeTreatmentRoomBedAliases(bedAliases ?? previousFavoriteData.bedAliases),
    showTreatmentMemo: options?.showTreatmentMemo ?? previousFavoriteData.showTreatmentMemo ?? false,
    showDetachedRemainingOverlay: options?.showDetachedRemainingOverlay ?? previousFavoriteData.showDetachedRemainingOverlay ?? false,
    treatmentItems: treatmentItems || DEFAULT_TREATMENT_ITEMS,
  }

  if (favorite) {
    return updateHospitalFavorite(favorite.hospitalFavoriteUuid, { hospitalFavoriteData: favoriteData })
  }

  return createHospitalFavorite({
    hospitalFavoriteName: TREATMENT_ROOM_FAVORITE_NAME,
    hospitalFavoriteData: favoriteData,
  })
}

